import { type BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react'
import { type LoginResponseData, type LoginRequestData, SignupRequest, ResearcherInviteData } from '../../types'
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { createAxiosInstance } from '../../api'
import { AuthError } from '../../errors'

export const apiAuthBaseQuery =
  (): BaseQueryFn<
    {
      url: string
      method: AxiosRequestConfig['method']
      data?: AxiosRequestConfig['data']
      params?: AxiosRequestConfig['params']
      headers?: AxiosRequestConfig['headers']
    },
    unknown,
    unknown
  > =>
  async ({ url, method, data, params, headers }) => {
    const api = createAxiosInstance(`${String(process.env.REACT_APP_ENGINE_API_URL)}/auth`)

    try {
      const result = await api(url, {
        method,
        data,
        params,
        headers,
      })

      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError
      console.error(err)

      const errorResponse: AxiosResponse | undefined = err.response
      const errorResponseData: Record<string, any> | undefined =
        errorResponse && errorResponse.data ? errorResponse.data : {}
      const errorMessage: string = errorResponseData && errorResponseData.message ? errorResponseData.message : ''

      if (errorMessage) {
        throw new AuthError(errorMessage)
      }

      throw axiosError
    }
  }

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: apiAuthBaseQuery(),
  endpoints: (builder) => ({
    loginUser: builder.mutation<LoginResponseData, { data: LoginRequestData }>({
      query({ data }) {
        return {
          url: '/login',
          method: 'post',
          data,
        }
      },
    }),
    registerOrganization: builder.mutation<string, { data: SignupRequest }>({
      query({ data }) {
        return {
          url: '/register',
          method: 'post',
          data,
        }
      },
    }),
    getInviteRequestDataByToken: builder.query<ResearcherInviteData, string>({
      query(token: string) {
        return {
          url: `/invite-request/${token}`,
          method: 'get',
        }
      },
    }),
  }),
})

export const { useLoginUserMutation, useRegisterOrganizationMutation, useLazyGetInviteRequestDataByTokenQuery } =
  authApi
